/**
 * Отказ сервера редактора — словами сервера.
 *
 * FastAPI кладёт причину в `{detail}`, и у наших ручек она бывает четырёх видов: строка
 * (`HTTPException(400, str(e))`), объект `{reason, message}` (409/422: «нет перевода», «время
 * реплики испорчено», «в интервале нет речи»), `{error, reason}` (503 словаря) и список ошибок
 * разбора тела (422 pydantic). Переводчику показывается текст, а машинный код причины (`reason`)
 * остаётся у вызывающего: по нему редактор решает, что делать дальше (например, 409
 * `translation_exists` — не ошибка для машинного перевода после склейки).
 *
 * Раньше половина обёрток не смотрела на статус вовсе: тело ошибки считалось данными, а
 * «Saved ✓» горел над несохранённым текстом.
 */

export type ApiError = Error & { status?: number; reason?: string; detail?: unknown };

const parse = (text: string | null): any => {
    if (!text) return undefined;
    try {
        return JSON.parse(text);
    } catch {
        return undefined;
    }
};

const detailText = (detail: any): string | null => {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        const messages = detail.map((item) => item?.msg).filter((msg) => typeof msg === "string");
        return messages.length ? messages.join("; ") : null;
    }
    if (detail && typeof detail === "object") {
        if (typeof detail.message === "string") return detail.message;
        // 503 словаря: {error: "glossary_unavailable", reason: "<слова>"}.
        if (typeof detail.reason === "string" && typeof detail.error === "string") return detail.reason;
        if (typeof detail.error === "string") return detail.error;
        if (typeof detail.reason === "string") return detail.reason;
    }
    return null;
};

/** Текст причины для человека; не JSON — тело как есть. */
export const extractErrorDetail = (text: string | null): string | null => {
    if (!text) return null;
    const body = parse(text);
    if (body === undefined) return text;
    return detailText(body?.detail) ?? text;
};

/** Машинный код причины (`detail.reason` наших 409/422), если сервер его назвал. */
const extractReasonCode = (text: string | null): string | undefined => {
    const detail = parse(text)?.detail;
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
        if (typeof detail.error === "string") return detail.error;
        if (typeof detail.reason === "string") return detail.reason;
    }
    return undefined;
};

export const throwIfNotOk = async (response: Response, fallback: string) => {
    if (response.ok) return;
    const text = await response.text().catch(() => null);
    const error: ApiError = new Error(
        extractErrorDetail(text) || `${fallback} (HTTP ${response.status})`,
    );
    error.status = response.status;
    error.reason = extractReasonCode(text);
    error.detail = parse(text)?.detail;
    throw error;
};

/** Код причины из пойманной ошибки (или undefined — сеть, клиент, старый сервер). */
export const errorReason = (error: unknown): string | undefined =>
    (error as ApiError | undefined)?.reason;
